/* Training courses — "How To Use Faitharama" series.
   6 short, plain-English courses that teach members how to use every
   tool inside the platform. Written in Mike's voice — Southern, plain,
   5th-grade reading level. No LLM calls. No outside links.

   Each course has 6 lessons. Each lesson has a short body of paragraphs
   and (optionally) a bulleted list. Rendered by TrainingCoursePage.jsx. */

export const TRAINING_COURSES = [
  /* ─────────── 0. FUNNEL BUILDER ─────────── */
  {
    slug: "funnel-builder-101",
    number: "01",
    title: "Funnel Builder 101",
    subtitle: "Build A Page That Sells — Without Knowing Code",
    blurb:
      "How to drag-and-drop a real sales page in under 30 minutes. No designer. No coder. No monthly extra fees.",
    accent: "#2E9F3A",
    accentSoft: "#5BD068",
    icon: "Layers",
    minutes: 35,
    chapters: [
      {
        n: 1,
        title: "What A Funnel Is (Plain English)",
        body: [
          { type: "p", text: "A funnel is just a page that walks one person, one step at a time, toward one decision. That's it. The headline grabs them. The video or the picture builds trust. The bullet list shows the value. The button collects either their email, their money, or both. Old folks built these for centuries on a single piece of paper — we just made it scroll." },
          { type: "p", text: "You do NOT need five pages. You do NOT need a 'webinar.' For most of our members, ONE funnel page is what makes the first sale." },
        ],
      },
      {
        n: 2,
        title: "Opening The Builder",
        body: [
          { type: "p", text: "From your Dashboard, click Funnels in the left sidebar. Hit the big New Funnel button. Give it a name only YOU will see — something like 'Goli Gummies — Aunt Carol.' Pick a starting template (we recommend Lead Capture for your first one)." },
          { type: "p", text: "The builder opens up. Left side is your blocks. Middle is your live page. Right side is the settings for whatever block you tapped on. That is the whole machine." },
        ],
      },
      {
        n: 3,
        title: "The 6 Blocks You'll Actually Use",
        body: [
          { type: "list-bold", items: [
            ["Headline", "One sentence. What the visitor gets. Use big bold words."],
            ["Image or Video", "Their face matters. Use a real photo, not stock art."],
            ["Bullet List", "3 to 5 short benefits. Each one starts with a verb."],
            ["Buy / Lead Button", "Either pulls money (Buy) or pulls an email (Lead)."],
            ["Testimonial", "One real quote from one real person. Be honest."],
            ["Spacer", "Empty space. Easy on senior eyes. Use it generously."],
          ]},
        ],
      },
      {
        n: 4,
        title: "Adding Your Image (Step By Step)",
        body: [
          { type: "p", text: "Click any Image block. Click the gray Upload button on the right. Pick the photo off your computer or phone. Wait three seconds. It appears on your page. You can drag the corner to resize it." },
          { type: "p", text: "Tip from Mike: a smiling face beats a fancy logo every time. Use a real photo of yourself, your kitchen, your Bible, your dog — anything real." },
        ],
      },
      {
        n: 5,
        title: "Wiring The Buy Button",
        body: [
          { type: "p", text: "Click your button block. On the right, choose 'Pulls money — Buy Button.' Pick the product from the dropdown (every Faitharama product is already on the list). Save. Done. Stripe handles the rest." },
          { type: "p", text: "If you want a lead capture button instead (to grow your email list before selling), pick 'Pulls email — Lead Button.' Their email lands inside your Contacts tab automatically." },
        ],
      },
      {
        n: 6,
        title: "Publishing And Sharing",
        body: [
          { type: "p", text: "Top right of the builder: a big green PUBLISH button. Click it. You will see a link like faitharama.com/f/your-name/goli. That link is your funnel. Copy it. Text it to your sister. Post it on Facebook. Stick it in your YouTube description. Every visit is tracked under YOUR account." },
          { type: "callout", text: "One funnel done well will outpull twenty funnels done halfway. Build one. Watch it. Then build the next." },
        ],
      },
    ],
  },

  /* ─────────── 2. AI WRITER (LONG-FORM) ─────────── */
  {
    slug: "ai-writer-101",
    number: "02",
    title: "AI Writer 101",
    subtitle: "Long-Form Writing — Articles, Chapters, Devotionals",
    blurb:
      "How to use the Pro / Unlimited long-form writer to draft 1,500–2,500 word articles, book chapters, and devotionals with real references.",
    accent: "#3B7DD8",
    accentSoft: "#6BA3EE",
    icon: "BookOpenText",
    minutes: 30,
    chapters: [
      {
        n: 1,
        title: "Pro & Unlimited Members Only",
        body: [
          { type: "p", text: "AI Writer is the big brother to AI Writer. Where AI Writer handles short pieces (under 400 words), AI Writer goes long — 1,500 to 2,500 words at a time. It cites Scripture, weighs the topic, and writes in your reading level." },
          { type: "p", text: "You'll only see it in your sidebar if you are on Pro ($49) or Unlimited ($79). Free, Trial, and Intermediate members do not have access — that's the whole reason this tool exists at the top tiers." },
        ],
      },
      {
        n: 2,
        title: "The 4 Modes",
        body: [
          { type: "list-bold", items: [
            ["Article", "Plain article. Best for blog posts and email newsletters."],
            ["Chapter", "Book chapter. Pulls in your prior chapters as context."],
            ["Devotional", "Short scripture-grounded reflection with prayer at the end."],
            ["Apologetics", "Defends a Christian truth with evidence, history, and Scripture."],
          ]},
          { type: "p", text: "Pick the mode FIRST. The whole prompt structure changes underneath." },
        ],
      },
      {
        n: 3,
        title: "Reading Level Matters",
        body: [
          { type: "p", text: "Right under the Mode picker is the Reading Level toggle. Set it to 6th grade if you're writing for the average reader. Set it to 8th if you want a bit more polish. College level if you are writing for pastors or apologists." },
          { type: "p", text: "Faitharama's default is 6th grade for a reason: more people understand it, more people share it, more people buy. Most preachers preach at 6th grade and they don't even know it." },
        ],
      },
      {
        n: 4,
        title: "Reference Books",
        body: [
          { type: "p", text: "Below the Mode picker is the Reference Books box. Add a book by title and author. Pro members can add up to 5 references. Unlimited members can add as many as they want. The writer will weave those sources into the article and credit them properly." },
          { type: "p", text: "Good defaults to keep in this box: 'Mere Christianity' by C.S. Lewis, 'The Case For Christ' by Lee Strobel, 'Knowing God' by J.I. Packer, and any book you wrote yourself." },
        ],
      },
      {
        n: 5,
        title: "Style: Paragraphs or Short Lines",
        body: [
          { type: "list-bold", items: [
            ["Paragraphs", "Standard prose. Best for blog and book chapters."],
            ["Short lines", "One thought per line. Best for emails and Facebook posts. Easier on senior eyes."],
          ]},
          { type: "p", text: "Tip from Mike: if you're sending it to email or social, pick short lines. If you're writing for your book or your site, pick paragraphs." },
        ],
      },
      {
        n: 6,
        title: "Saving And Re-Using",
        body: [
          { type: "p", text: "When the article is done, hit Save. Every saved article lives in My Articles in your sidebar. From there you can edit it, copy it, export it to PDF, or send it straight into the Funnel Builder as a landing page." },
          { type: "p", text: "Daily caps: 20 long-form articles per day on Pro, 60 per day on Unlimited. Each article counts as ONE no matter how long it is." },
        ],
      },
    ],
  },

  /* ─────────── 3. MIRROR SITE ─────────── */
  {
    slug: "mirror-site-101",
    number: "03",
    title: "Mirror Site 101",
    subtitle: "Your Personal Storefront — Built In 5 Minutes",
    blurb:
      "Every member gets a personal Faitharama storefront at /m/your-handle. Every product is pre-tagged with YOUR affiliate code. This is the easiest first link to share.",
    accent: "#C9202E",
    accentSoft: "#E25462",
    icon: "Globe",
    minutes: 20,
    chapters: [
      {
        n: 1,
        title: "What A Mirror Site Is",
        body: [
          { type: "p", text: "A Mirror Site is a copy of the Faitharama storefront — but with YOUR name, YOUR photo, and YOUR commissions baked into every product link. When somebody buys from your Mirror Site, the platform automatically credits your account." },
          { type: "p", text: "You do not have to register a domain. You do not have to point DNS at anything. You do not pay a monthly hosting fee. It's already built. You just turn it on." },
        ],
      },
      {
        n: 2,
        title: "Picking Your Handle",
        body: [
          { type: "p", text: "From your Dashboard, click Mirror Site in the left sidebar. The first thing it asks for is your handle — that's the word that goes at the end of your link." },
          { type: "p", text: "Examples: faitharama.com/m/pastor-mike. faitharama.com/m/granny-grace. faitharama.com/m/sundayschool-dan. Use letters, numbers, and dashes only. Keep it short. Keep it memorable." },
        ],
      },
      {
        n: 3,
        title: "Your Photo And Bio",
        body: [
          { type: "p", text: "Upload a head-and-shoulders photo of yourself. Smile. Good lighting. Real you — not your dog, not a stock photo. Below the photo, write a short bio: 1 sentence about who you are, 1 sentence about what you believe, 1 sentence about why you joined Faitharama." },
          { type: "p", text: "Three sentences is the sweet spot. Anything longer and visitors skip it." },
        ],
      },
      {
        n: 4,
        title: "Picking Featured Products",
        body: [
          { type: "p", text: "Below the bio is the Featured Products picker. Drag the 3 to 5 products you most believe in into the top row. Those will show up at the top of your storefront. The rest of the catalog still appears underneath." },
          { type: "p", text: "Mike's pick rule: only feature products you have actually used yourself. If you've never opened a Goli bottle, don't feature Goli." },
        ],
      },
      {
        n: 5,
        title: "Sharing Your Mirror Link",
        body: [
          { type: "list-bold", items: [
            ["Text to family", "One-line text: 'Hey — here's my new little store. Take a look.' + link."],
            ["Email signature", "Stick your link at the bottom of every email forever."],
            ["Facebook profile", "Edit your profile, paste your link in the Website field."],
            ["Church bulletin board", "Print a small flyer with the link + a QR code (Mirror Site auto-generates it)."],
            ["Business cards", "$15 at Vistaprint. Link + your face. Hand them out at church."],
          ]},
        ],
      },
      {
        n: 6,
        title: "Tracking Your Visits And Sales",
        body: [
          { type: "p", text: "Inside the Mirror Site tab you'll see two numbers updated every hour: Visits (anybody who clicked your link) and Sales (anybody who actually bought through your link)." },
          { type: "p", text: "Don't watch it like a hawk. Refresh it once a day. Most members get their first sale somewhere between day 14 and day 45 — long before that, just keep sharing the link." },
        ],
      },
    ],
  },

  /* ─────────── 4. MY PRODUCTS ─────────── */
  {
    slug: "my-products-101",
    number: "04",
    title: "My Products 101",
    subtitle: "Selling Your Own PDFs, Books, And Digital Goods",
    blurb:
      "Already wrote a book? Got a PDF guide? Built your own course? Upload it, set a price, and Faitharama handles the checkout, delivery, and your money.",
    accent: "#7A4DD3",
    accentSoft: "#A88AE8",
    icon: "Briefcase",
    minutes: 25,
    chapters: [
      {
        n: 1,
        title: "What You Can Sell",
        body: [
          { type: "list-bold", items: [
            ["PDF books", "Your own ebooks, study guides, devotionals, recipe books."],
            ["Audio files", "MP3 sermons, audio devotionals, audio versions of your books."],
            ["Video courses", "MP4 lessons (bundled into a course)."],
            ["Bundles", "A book + audio + worksheet sold together at one price."],
          ]},
          { type: "p", text: "What you CANNOT sell: physical products (we don't do shipping), MLM downlines, or anything we'd be ashamed to share with our grandmother. Standard rule." },
        ],
      },
      {
        n: 2,
        title: "Uploading Your First Product",
        body: [
          { type: "p", text: "From your Dashboard, click My Products. Hit the big NEW PRODUCT button. Fill in the title. Write a short description (4–6 sentences). Set a price ($7, $19, $27, $47 are the most common). Upload your cover image (a screenshot of the front of your book works great)." },
          { type: "p", text: "Then upload the actual file — your PDF, your MP3, your MP4. Hit SAVE. The product is live within 30 seconds." },
        ],
      },
      {
        n: 3,
        title: "Your Product Page",
        body: [
          { type: "p", text: "Faitharama automatically builds a sales page for every product you upload. It pulls your cover, your description, and your price. The page is mobile-friendly and the checkout button is a real Stripe button — no setup required." },
          { type: "p", text: "You can preview it before going live: hit the eyeball icon next to your product in the My Products tab." },
        ],
      },
      {
        n: 4,
        title: "Money And Splits",
        body: [
          { type: "list-bold", items: [
            ["You keep 80%", "Faitharama keeps 20% as the platform fee."],
            ["Affiliate sales", "If another member sells your product via THEIR Mirror Site, they earn their tier (25% Free / 60% Paid). You keep the rest after the platform fee."],
            ["Payouts", "Weekly to your bank account. Stripe minimum is $20."],
          ]},
          { type: "p", text: "Translation: a $19 book sold direct = $15.20 to you. Sold through an affiliate = roughly $7 to you, $8 to the affiliate, $4 to the platform." },
        ],
      },
      {
        n: 5,
        title: "Delivery To Your Customer",
        body: [
          { type: "p", text: "After purchase, the customer gets two things: an instant download link on the thank-you page, AND an email with the same link. They can re-download it as many times as they want for as long as you keep the product live." },
          { type: "p", text: "No fulfillment to handle. No 'I lost the file, can you resend it' emails — they always have access." },
        ],
      },
      {
        n: 6,
        title: "Updating, Pausing, And Removing",
        body: [
          { type: "p", text: "From the My Products tab, click any product to edit. You can change the price, swap the cover, upload a new version of the file (everyone who already bought gets a free upgrade), or pause sales temporarily." },
          { type: "p", text: "Removing a product hides it from the storefront, but past customers can still re-download — that's part of the deal. Be slow to remove." },
        ],
      },
    ],
  },

  /* ─────────── 5. EMAIL TOOLS ─────────── */
  {
    slug: "email-tools-101",
    number: "05",
    title: "Email Tools 101",
    subtitle: "Your List Is The Most Valuable Thing You'll Build",
    blurb:
      "How to collect emails, write weekly broadcasts, set up welcome sequences, and stay inside the 'be a good neighbor' rules.",
    accent: "#0F172A",
    accentSoft: "#3B5085",
    icon: "Mail",
    minutes: 30,
    chapters: [
      {
        n: 1,
        title: "Why The Email List Matters Most",
        body: [
          { type: "p", text: "Facebook can ban you. YouTube can shadow-ban you. Google can rank you on page 9. None of those are yours. An email list IS yours. When you have somebody's email and their permission, you can reach them whenever — for free — for as long as they want to hear from you." },
          { type: "p", text: "If you only do ONE thing on Faitharama, do this: build the list." },
        ],
      },
      {
        n: 2,
        title: "Collecting Emails (3 Built-in Ways)",
        body: [
          { type: "list-bold", items: [
            ["Lead Button in your Funnel", "Set any funnel button to Pulls email — Lead Button. Email lands in your Contacts."],
            ["Mirror Site signup box", "Already live on every Mirror Site. Visitors join your list when they ask for updates."],
            ["My Products thank-you page", "Every buyer is auto-added to your list (with their permission, by checkbox)."],
          ]},
        ],
      },
      {
        n: 3,
        title: "Writing Your First Broadcast",
        body: [
          { type: "p", text: "From your Dashboard, click Email Tools. Hit NEW BROADCAST. Write a subject line (AI Writer can suggest 10 if you're stuck). Write a short body — 100 to 200 words is plenty. Pick which Contacts list to send to. Hit SEND or SCHEDULE." },
          { type: "p", text: "The cleanest first email: introduce yourself, share ONE useful piece of advice, and tell them they can hit Reply anytime. That's the whole email." },
        ],
      },
      {
        n: 4,
        title: "The Welcome Sequence",
        body: [
          { type: "p", text: "A Welcome Sequence is 3 to 5 emails that go out automatically to anyone who joins your list. Email 1 today. Email 2 tomorrow. Email 3 three days later. And so on." },
          { type: "list", items: [
            "Email 1: 'Welcome — here's who I am, here's what to expect.'",
            "Email 2: 'My best free tip on [your topic].'",
            "Email 3: 'A real story about how I learned this.'",
            "Email 4: 'The one product I trust above all others' (your top affiliate).",
            "Email 5: 'How I'd start over if I had to start today.'",
          ]},
          { type: "p", text: "Set it once. It works for every new subscriber forever." },
        ],
      },
      {
        n: 5,
        title: "Fair-Use Rules (Be A Good Neighbor)",
        body: [
          { type: "list-bold", items: [
            ["12 emails per contact per month, max", "More than that and you're spam, plain and simple."],
            ["One product mention per 3 emails", "The other 2 should be genuinely useful — recipes, scripture, advice."],
            ["Always include 'Unsubscribe'", "Faitharama auto-adds it. Don't try to hide it."],
            ["Never buy email lists", "Permanent ban from the platform. We mean it."],
          ]},
        ],
      },
      {
        n: 6,
        title: "Reading Your Email Stats",
        body: [
          { type: "p", text: "Every broadcast you send shows three numbers afterwards: Opens (how many people saw it), Clicks (how many tapped a link), and Sales (how many bought as a result). A healthy open rate for our audience is 35-45%. A healthy click rate is 5-10%." },
          { type: "p", text: "Don't obsess over numbers. Pick the ONE email per month that worked best, and write more like that one." },
        ],
      },
    ],
  },
];
